<template>
    <rkm-area
        background-color="primary"
        header-prefix-text="O"
        title="Objetivo"
        type="box"
    >
        <rkm-row>
            <rkm-col cols="12">
                <rkm-text-editor
                    :validations="validations.descricao"
                    name="Descrição"
                    placeholder="Insira as demais informações relacionadas ao exame físico."
                    v-model="data.model.descricao"
                />
            </rkm-col>
        </rkm-row>

        <template v-if="data.additionals.isAtendimentoPreNatal">
            <rkm-area
                v-if="data.form.idadeGestacional"
                no-margin-top
                title="Idade gestacional"
            >
                <rkm-row>
                    <rkm-col cols="12">
                        <rkm-form-input
                            :value="`${data.form.idadeGestacional.semanas}s ${data.form.idadeGestacional.dias}d`"
                            disabled
                            label="Semanas / Dias"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>

            <rkm-area
                no-margin-top
                title="Último atendimento de pré-natal"
            >
                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="data.form.ultimoAtendimentoPreNatal ? data.form.ultimoAtendimentoPreNatal.data : null"
                            disabled
                            label="Data"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-form-input
                            :value="data.form.ultimoAtendimentoPreNatal ? data.form.ultimoAtendimentoPreNatal.usuarioNome : null"
                            disabled
                            label="Profissional"
                        />
                    </rkm-col>
                </rkm-row>
            </rkm-area>
        </template>

        <template v-if="data.additionals.paciente.isCrianca || data.additionals.paciente.isAdolescente">
            <rkm-row>
                <rkm-col
                    class="mt-1"
                    cols="auto"
                >
                    <rkm-form-radio-group
                        @change="handlePuericulturaChange"
                        class="m-0"
                        label="Puericultura"
                        v-model="data.model.isAtendimentoPuericultura"
                    >
                        <rkm-form-radio :value="true">Sim</rkm-form-radio>
                        <rkm-form-radio :value="false">Não</rkm-form-radio>
                    </rkm-form-radio-group>
                </rkm-col>
            </rkm-row>

            <div v-show="data.model.isAtendimentoPuericultura">
                <BoxPreNatalPartoNascimento
                    v-if="data.additionals.isPrimeiroAtendimentoPuericultura"
                    :data="data"
                />

                <rkm-row v-else>
                    <rkm-col cols="12">
                        <rkm-alert color="primary">
                            Pré-natal, parto e nascimento já informados.
                        </rkm-alert>
                    </rkm-col>
                </rkm-row>

                <rkm-row v-if="data.additionals.paciente.isCriancaAleitamento && !data.additionals.isPrimeiroAtendimentoPuericultura">
                    <rkm-col cols="12">
                        <rkm-form-radio-group
                            :options="data.form.puericultura.aleitamentoTipos"
                            class="mb-0 mt-1"
                            label="Tipo de aleitamento"
                            text-field="descricao"
                            v-model="data.model.puericultura.aleitamentoTipoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <BoxDesenvolvimentoCrianca
                    v-if="data.additionals.paciente.isCrianca"
                    :data="data"
                />
            </div>
        </template>

        <template v-if="data.additionals.isAtendimentoPreNatal && !data.additionals.isPrimeiroAtendimentoPreNatal">
            <rkm-row>
                <rkm-col
                    class="mt-1"
                    cols="auto"
                >
                    <rkm-form-radio-group
                        @change="handlePreNatalChange"
                        class="m-0"
                        label="Pré-natal"
                        v-model="model.isAtendimentoPreNatal"
                    >
                        <rkm-form-radio :value="true">Sim</rkm-form-radio>
                        <rkm-form-radio :value="false">Não</rkm-form-radio>
                    </rkm-form-radio-group>
                </rkm-col>
            </rkm-row>

            <BoxPreNatal
                v-show="model.isAtendimentoPreNatal"
                :data="data"
            />
        </template>

        <template v-else>
            <rkm-row
                v-if="data.additionals.paciente.sexoId === enums.sexo.FEMININO && !data.additionals.paciente.isCrianca"
                class="mt-2"
            >
                <rkm-col cols="2">
                    <rkm-form-date-picker
                        :max-date="data.additionals.dataAtual"
                        :min-date="data.additionals.paciente.dataNascimento"
                        :validations="validations.dum"
                        label="DUM"
                        v-model="data.model.dum"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-date-picker
                        :value="data.form.ultimaDum ? others.moment(data.form.ultimaDum, 'DD/MM/YYYY').toDate() : null"
                        disabled
                        label="Última DUM"
                    />
                </rkm-col>
            </rkm-row>
        </template>

        <BoxAntropometria
            :data="data"
            closed
        />

        <BoxSinaiVital
            :data="data"
        />

        <BoxGlicemiaCapilar
            :data="data"
            closed
        />

        <rkm-area
            v-if="data.form.coletasMateriais.length > 0"
            title="Coletas realizadas"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col cols="12">
                    {{data.form.coletasMateriais.join(', ')}}
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area
            v-if="data.form.outrosProcedimentos.length > 0"
            title="Procedimentos realizados"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col cols="12">
                    {{data.form.outrosProcedimentos.join(', ')}}
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area
            v-if="data.form.administracoesMedicamentos.length > 0"
            title="Medicamentos administrados por via"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col cols="12">
                    {{data.form.administracoesMedicamentos.join(', ')}}
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <BoxMarcadoresConsumoAlimentar
            v-if="data.additionals.showMarcadorConsumoAlimentar"
            :data="data"
            closed
        />

        <rkm-row class="mt-2">
            <rkm-col cols="auto">
                <rkm-form-radio-group
                    label="Vacinação em dia"
                    v-model="data.model.vacinacaoEmDia"
                >
                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                    <rkm-form-radio :value="null">Não informado</rkm-form-radio>
                </rkm-form-radio-group>
            </rkm-col>

            <rkm-col>
                <rkm-button
                    :label="false"
                    @click="printCarteiraVacinacao"
                    color="primary"
                >
                    Visualizar carteirinha de vacinação
                </rkm-button>
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="data.additionals.isAtendimentoSaudeBucal">
            <rkm-col cols="12">
                <rkm-form-radio-group
                    label="Paciente com necessidades especiais"
                    v-model="data.model.pacienteComNecessidadeEspecial"
                >
                    <rkm-form-radio :value="true">Sim</rkm-form-radio>
                    <rkm-form-radio :value="false">Não</rkm-form-radio>
                </rkm-form-radio-group>
            </rkm-col>
        </rkm-row>

        <BoxResultadoExame
            :data="data"
            class="mt-2"
        />
    </rkm-area>
</template>

<script>
// Components.
import BoxAntropometria from '@/components/ambulatorio/components/antropometria/Index'
import BoxDesenvolvimentoCrianca from './components/desenvolvimento-crianca/Index'
import BoxGlicemiaCapilar from '@/components/ambulatorio/components/glicemia-capilar/Index'
import BoxMarcadoresConsumoAlimentar from './components/marcador-consumo-alimentar/Index'
import BoxPreNatal from '@/components/ambulatorio/components/pre-natal/Index'
import BoxPreNatalPartoNascimento from '@/components/ambulatorio/components/pre-natal-parto-nascimento/Index'
import BoxResultadoExame from './components/resultado-exame/Index'
import BoxSinaiVital from '@/components/ambulatorio/components/sinal-vital/Index'

// Enums.
import sexo from '@/enums/cadastro/cidadao/sexo'

// Mixins.
import {ambulatorioAtendimentoPreNatal, ambulatorioAtendimentoProblemaCondicao, file, object, repository} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {
        BoxAntropometria,
        BoxDesenvolvimentoCrianca,
        BoxGlicemiaCapilar,
        BoxMarcadoresConsumoAlimentar,
        BoxPreNatal,
        BoxPreNatalPartoNascimento,
        BoxResultadoExame,
        BoxSinaiVital,
    },
    computed: {
        validations() {
            return {
                descricao: {max: 5000},
                dum: {required: this.data.additionals.isPrimeiroAtendimentoPreNatal},
            }
        },
    },
    created() {
        this.puericultura = this.clone(this.data.model.puericultura)

        this.repositories = {
            cid: this.getConectaSusRepository('sigtap.cid'),
            geral: this.getConectaSusRepository('geral'),
            impressaoImunizacao: this.getConectaSusRepository('impressao.imunizacao'),
        }
    },
    data: () => ({
        enums: {sexo},
        model: {
            isAtendimentoPreNatal: false,
        },
        others: {moment},
        puericultura: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        handlePuericulturaChange(value) {
            if (value) {
                this.includeProblemaCondicaoPuericultura()
            }
            else if (!value) {
                this.showWarningConfirmation(
                    'Deseja descartar os dados de Puericultura?',
                    'As alterações realizadas serão perdidas.',
                    async () => {
                        this.getParcialProblemasCondicoesPuericultura().forEach(problemaCondicaoPuericultura => {
                            this.data.avaliacaoModel.problemasCondicoes.splice(this.data.avaliacaoModel.problemasCondicoes.indexOf(problemaCondicaoPuericultura), 1)
                        })

                        this.resetFormPuericultura()
                    },
                    async () => this.data.model.isAtendimentoPuericultura = true
                )
            }
        },
        resetFormPuericultura() {
            this.data.model.puericultura = this.puericultura
        },
        handlePreNatalChange(value) {
            if (value && !this.data.additionals.hasGravidezNormal) {
                if (this.data.additionals.hasDesfechoGravidez) {
                    this.model.isAtendimentoPreNatal = false

                    this.showValidationNotification('Para registrar pré-natal remova a condição de desfecho de gravidez.')

                    return
                }

                const problemaCondicaoPaciente = this.getPreparedProblemaCondicaoPaciente(this.data.additionals.problemaCondicaoPacienteGravidezNormal)

                problemaCondicaoPaciente.id = problemaCondicaoPaciente.problemaCondicaoPacienteId
                delete problemaCondicaoPaciente.problemaCondicaoPacienteId

                this.data.avaliacaoModel.problemasCondicoes.push(problemaCondicaoPaciente)
            }
            else if (!value) {
                this.showWarningConfirmation(
                    'Confirmação de exclusão',
                    'Deseja descartar os dados de pré-natal? Os campos de pré-natal serão desabilitados, os dados contidos nos mesmos serão perdidos e os problemas e/ou condições de gestação serão excluídos da avaliação.',
                    async () => {
                        const problemaCondicaoGravidezNormal = this.data.additionals.problemaCondicaoGravidezNormal
                        const problemasCondicoesGravidezAltoRisco = this.data.additionals.problemasCondicoesGravidezAltoRisco

                        this.data.avaliacaoModel.problemasCondicoes.splice(this.data.avaliacaoModel.problemasCondicoes.indexOf(problemaCondicaoGravidezNormal), 1)

                        problemasCondicoesGravidezAltoRisco.forEach(problemaCondicaoGravidezAltoRisco => {
                            this.data.avaliacaoModel.problemasCondicoes.splice(this.data.avaliacaoModel.problemasCondicoes.indexOf(problemaCondicaoGravidezAltoRisco), 1)
                        })

                        this.resetFormPreNatal()
                    },
                    async () => this.model.isAtendimentoPreNatal = true
                )
            }
        },
        resetFormPreNatal() {
            this.data.model.preNatal.alturaUterina = null
            this.data.model.preNatal.gravidezAltoRisco = null
            this.data.model.preNatal.edemaId = null
            this.data.model.preNatal.movimentacaoFetal = null
            this.data.model.preNatal.gravidezPlanejada = null
            this.data.model.preNatal.batimentoCardiacoFetal = null
            this.data.model.preNatal.desfechoEm = null
            this.data.model.dum = null
        },
        includeProblemaCondicaoPuericultura() {
            if (!this.hasProblemasCondicoesPuericultura()) {
                Promise.all([
                    this.getCiapByCodigo(this.enums.ciap.MEDICINA_PREVENTIVA_MANUTENCAO_DA_SAUDE),
                    this.getCidByCodigo(this.enums.cid.EXAME_DE_ROTINA_DE_SAUDE_DA_CRIANCA),
                ]).then(([responseCiap, responseCid]) => {
                    const ciap = responseCiap.data[0]
                    const cid = responseCid.data[0]

                    const problemaCondicao = this.getPreparedProblemaCondicao({
                        ciap: {
                            id: ciap.id,
                            codigo: ciap.codigo,
                            descricao: ciap.descricao,
                        },
                        cid: {
                            id: cid.id,
                            codigo: cid.codigo,
                            nome: cid.nome,
                        },
                    })

                    this.data.avaliacaoModel.problemasCondicoes.push(problemaCondicao)
                })
            }
        },
        async getCiapByCodigo(codigo) {
            this.setLoading()

            try {
                return await this.repositories.geral.ciap.index({codigo})
            }
            catch (error) {
                this.handleError(error)
            }
            finally {
                this.unsetLoading()
            }
        },
        async getCidByCodigo(codigo) {
            this.setLoading()

            try {
                return await this.repositories.cid.index({codigo})
            }
            catch (error) {
                this.handleError(error)
            }
            finally {
                this.unsetLoading()
            }
        },
        hasProblemasCondicoesPuericultura() {
            return this.getProblemasCondicoesPuericultura().length > 0
        },
        getProblemasCondicoesPuericultura() {
            return this.data.avaliacaoModel.problemasCondicoes.filter(problemaCondicao => {
                let isCiapPuericultura = false
                let isCidPuericultura = false

                if (problemaCondicao.ciap) {
                    isCiapPuericultura = problemaCondicao.ciap.codigo === this.enums.ciap.MEDICINA_PREVENTIVA_MANUTENCAO_DA_SAUDE
                }

                if (problemaCondicao.cid) {
                    isCidPuericultura = problemaCondicao.cid.codigo === this.enums.cid.EXAME_DE_ROTINA_DE_SAUDE_DA_CRIANCA
                }

                return isCiapPuericultura && isCidPuericultura
            })
        },
        getParcialProblemasCondicoesPuericultura() {
            return this.data.avaliacaoModel.problemasCondicoes.filter(problemaCondicao => {
                let isCiapPuericultura = false
                let isCidPuericultura = false

                if (problemaCondicao.ciap) {
                    isCiapPuericultura = problemaCondicao.ciap.codigo === this.enums.ciap.MEDICINA_PREVENTIVA_MANUTENCAO_DA_SAUDE
                }

                if (problemaCondicao.cid) {
                    isCidPuericultura = problemaCondicao.cid.codigo === this.enums.cid.EXAME_DE_ROTINA_DE_SAUDE_DA_CRIANCA
                }

                return isCiapPuericultura || isCidPuericultura
            })
        },
        printCarteiraVacinacao() {
            this.setLoading()

            this.repositories.impressaoImunizacao.carteiraVacinacao(this.data.additionals.paciente.id)
                .then(response => this.openFile(response.data, response.headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mounted() {

        if (this.data.model.preNatal.isRascunhoAtendimentoPreNatal) {

            this.model.isAtendimentoPreNatal = true
        }
    },
    mixins: [ambulatorioAtendimentoPreNatal, ambulatorioAtendimentoProblemaCondicao, file, object, repository],
    props: {
        data: {type: Object, required: true},
    },
    watch: {
        'data.additionals.hasGravidezNormal'(value) {
            if (!this.data.additionals.hasGravidezAltoRisco) {
                this.model.isAtendimentoPreNatal = value
            }
        },
        'data.additionals.hasGravidezAltoRisco'(value) {
            if (!this.data.additionals.hasGravidezNormal) {
                this.model.isAtendimentoPreNatal = value
            }
        },
    },
}
</script>